import type { ReactNode, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../components/ui/Card';
import PublicSiteChrome from '../components/layout/PublicSiteChrome';
import { usePageMeta } from '../lib/page-meta';
import { useAuth } from '../hooks/useAuth';
import { buildOrganizationSchema, buildWebsiteSchema, buildWebPageSchema } from '../lib/seo';

const DEMO_REQUEST_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Demo%20Request';

interface SectionCopy {
  title: string;
  description: string;
}

interface CapabilityCopy extends SectionCopy {
  id: string;
}

function SectionHead({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="acc-public-section-head">
      <div className="acc-public-kicker">{eyebrow}</div>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function PublicButtonLink({
  href,
  children,
  variant = 'primary',
}: {
  href: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
}) {
  const className = `acc-public-button acc-public-button--${variant}`;

  if (href.startsWith('mailto:') || href.startsWith('/#') || href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

function MarketingIcon({ children }: { children: ReactNode }) {
  return <div className="acc-public-icon-wrap" aria-hidden="true">{children}</div>;
}

function FeatureIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M4 7.5h16M4 12h16M4 16.5h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="18" cy="16.5" r="2.3" fill="currentColor" />
    </svg>
  );
}

function ReportIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M7 4.75h7.6L19 9.1V19a1.75 1.75 0 0 1-1.75 1.75h-10.5A1.75 1.75 0 0 1 5 19V6.5A1.75 1.75 0 0 1 6.75 4.75Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M14.5 4.75V9h4.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 13h7M8.5 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function RouteIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="6" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="18" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.2 7.8c1.5 1.5 2.9 2.4 4.4 3.1 1.7.8 2.8 1.4 4 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M17.4 6h-3.3v3.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.1 9.3 18 5.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AlertIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M12 4.5a4 4 0 0 1 4 4V10c0 .9.3 1.7.8 2.4l1.1 1.5a1.2 1.2 0 0 1-.97 1.91H7.08a1.2 1.2 0 0 1-.97-1.91l1.09-1.5c.52-.7.8-1.55.8-2.4V8.5a4 4 0 0 1 4-4Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 18a2.3 2.3 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function EvidenceIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m8 14 2.5-2.5 2.3 2.3L15.5 11l2.5 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="9.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

function PortfolioIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="4.5" y="5" width="15" height="5.5" rx="1.8" stroke="currentColor" strokeWidth="1.8" />
      <rect x="4.5" y="13.5" width="7" height="5.5" rx="1.8" stroke="currentColor" strokeWidth="1.8" />
      <rect x="13" y="13.5" width="6.5" height="5.5" rx="1.8" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

const CAPABILITY_ICONS: Record<string, ReactNode> = {
  automatedScans: <FeatureIcon />,
  authenticatedRoutes: <RouteIcon />,
  evidence: <EvidenceIcon />,
  reporting: <ReportIcon />,
  alerts: <AlertIcon />,
  portfolio: <PortfolioIcon />,
};

export default function LandingPage() {
  const { t } = useTranslation('marketing');
  const { isAuthenticated } = useAuth();

  const capabilities = t('landing.capabilities.items', { returnObjects: true }) as CapabilityCopy[];
  const steps = t('landing.steps.items', { returnObjects: true }) as SectionCopy[];
  const audiences = t('landing.audience.items', { returnObjects: true }) as SectionCopy[];
  const heroPills = t('landing.hero.pills', { returnObjects: true }) as string[];
  const previewMiniList = t('landing.preview.miniList', { returnObjects: true }) as SectionCopy[];
  const problemAvoidItems = t('landing.problem.avoid.items', { returnObjects: true }) as string[];
  const problemLayerItems = t('landing.problem.layer.items', { returnObjects: true }) as string[];
  const trustReviewItems = t('landing.trust.review.items', { returnObjects: true }) as string[];
  const trustCards = t('landing.trust.cards', { returnObjects: true }) as SectionCopy[];

  usePageMeta({
    title: t('landing.meta.title'),
    description: t('landing.meta.description'),
    canonicalPath: '/',
    structuredData: [
      buildOrganizationSchema(t('landing.meta.description')),
      buildWebsiteSchema(t('landing.meta.description')),
      buildWebPageSchema({
        title: t('landing.hero.title'),
        description: t('landing.meta.description'),
        path: '/',
      }),
    ],
  });

  return (
    <PublicSiteChrome>
      <div className="acc-public-frame">
        <section className="acc-public-section" aria-labelledby="landing-hero-title">
          <div className="acc-public-hero">
            <div className="acc-public-hero-copy">
              <div className="acc-public-kicker">{t('landing.hero.kicker')}</div>
              <h1 id="landing-hero-title" className="acc-public-title">
                {t('landing.hero.title')}
              </h1>
              <p className="acc-public-subtitle">{t('landing.hero.subtitle')}</p>

              <div className="acc-public-hero-actions">
                <PublicButtonLink href={DEMO_REQUEST_HREF}>{t('actions.requestDemo')}</PublicButtonLink>
                <PublicButtonLink href="/pricing" variant="secondary">{t('actions.viewPricing')}</PublicButtonLink>
                {isAuthenticated && (
                  <PublicButtonLink href="/dashboard" variant="ghost">{t('actions.openDashboard')}</PublicButtonLink>
                )}
              </div>

              <div className="acc-public-supporting-line">{t('landing.hero.supportingLine')}</div>

              <div className="acc-public-pills" aria-label={t('landing.hero.highlightsLabel')}>
                {heroPills.map((pill) => (
                  <span key={pill} className="acc-public-pill">{pill}</span>
                ))}
              </div>
            </div>

            <div className="acc-public-hero-panel" aria-label={t('landing.hero.previewLabel')}>
              <div className="acc-public-preview">
                <div className="acc-public-preview-topbar">
                  <div>
                    <div className="acc-public-preview-eyebrow">{t('landing.preview.eyebrow')}</div>
                    <div className="acc-public-preview-title">{t('landing.preview.title')}</div>
                  </div>
                  <div className="acc-public-preview-status">{t('landing.preview.status')}</div>
                </div>

                <div className="acc-public-preview-grid">
                  <div className="acc-public-preview-card">
                    <h3>{t('landing.preview.cards.coverage.title')}</h3>
                    <p>{t('landing.preview.cards.coverage.description')}</p>
                  </div>
                  <div className="acc-public-preview-card">
                    <h3>{t('landing.preview.cards.issueReview.title')}</h3>
                    <p>{t('landing.preview.cards.issueReview.description')}</p>
                  </div>
                  <div className="acc-public-preview-card acc-public-preview-card--wide">
                    <h3>{t('landing.preview.cards.reporting.title')}</h3>
                    <p>{t('landing.preview.cards.reporting.description')}</p>
                    <div className="acc-public-mini-list">
                      {previewMiniList.map((item) => (
                        <div key={item.title} className="acc-public-mini-list-row">
                          <strong>{item.title}</strong>
                          <span>{item.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="problem" className="acc-public-section" aria-labelledby="problem-title">
          <SectionHead
            eyebrow={t('landing.problem.eyebrow')}
            title={t('landing.problem.title')}
            description={t('landing.problem.description')}
          />

          <div className="acc-public-problem-grid">
            <div className="acc-public-surface">
              <h3 id="problem-title">{t('landing.problem.avoid.title')}</h3>
              <p>{t('landing.problem.avoid.body')}</p>
              <ul className="acc-public-list">
                {problemAvoidItems.map((item) => (
                  <li key={item}><span className="acc-public-list-mark" />{item}</li>
                ))}
              </ul>
            </div>

            <div className="acc-public-surface">
              <h3>{t('landing.problem.layer.title')}</h3>
              <p>{t('landing.problem.layer.body')}</p>
              <ul className="acc-public-list">
                {problemLayerItems.map((item) => (
                  <li key={item}><span className="acc-public-list-mark acc-public-list-mark--accent" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="capabilities" className="acc-public-section" aria-labelledby="capabilities-title">
          <SectionHead
            eyebrow={t('landing.capabilities.eyebrow')}
            title={t('landing.capabilities.title')}
            description={t('landing.capabilities.description')}
          />

          <div className="acc-public-grid acc-public-grid--features">
            {capabilities.map((feature) => (
              <Card key={feature.id} className="acc-public-feature-card">
                <MarketingIcon>{CAPABILITY_ICONS[feature.id] ?? null}</MarketingIcon>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </Card>
            ))}
          </div>
        </section>

        <section id="how-it-works" className="acc-public-section" aria-labelledby="how-it-works-title">
          <SectionHead
            eyebrow={t('landing.steps.eyebrow')}
            title={t('landing.steps.title')}
            description={t('landing.steps.description')}
          />

          <div className="acc-public-grid acc-public-grid--steps">
            {steps.map((step, index) => (
              <Card key={step.title} className="acc-public-step-card">
                <div className="acc-public-step-number">{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="acc-public-section" aria-labelledby="trust-title">
          <SectionHead
            eyebrow={t('landing.trust.eyebrow')}
            title={t('landing.trust.title')}
            description={t('landing.trust.description')}
          />

          <div className="acc-public-trust-layout">
            <div className="acc-public-surface">
              <h3 id="trust-title">{t('landing.trust.review.title')}</h3>
              <p>{t('landing.trust.review.body')}</p>
              <ul className="acc-public-list">
                {trustReviewItems.map((item) => (
                  <li key={item}><span className="acc-public-list-mark acc-public-list-mark--accent" />{item}</li>
                ))}
              </ul>
              <div className="acc-public-trust-note">{t('landing.trust.review.note')}</div>
            </div>

            <div className="acc-public-trust-stack">
              {trustCards.map((card) => (
                <Card key={card.title} className="acc-public-trust-card">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="who-its-for" className="acc-public-section" aria-labelledby="audience-title">
          <SectionHead
            eyebrow={t('landing.audience.eyebrow')}
            title={t('landing.audience.title')}
            description={t('landing.audience.description')}
          />

          <div className="acc-public-grid acc-public-grid--audiences">
            {audiences.map((audience) => (
              <Card key={audience.title} className="acc-public-audience-card">
                <h3>{audience.title}</h3>
                <p>{audience.description}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="acc-public-section" aria-labelledby="final-cta-title">
          <div className="acc-public-cta-band">
            <div>
              <h2 id="final-cta-title">{t('landing.finalCta.title')}</h2>
              <p>{t('landing.finalCta.description')}</p>
            </div>
            <div className="acc-public-cta-actions">
              <PublicButtonLink href={DEMO_REQUEST_HREF}>{t('actions.requestDemo')}</PublicButtonLink>
              <PublicButtonLink href="/pricing" variant="secondary">{t('actions.viewPricing')}</PublicButtonLink>
            </div>
          </div>
        </section>
      </div>
    </PublicSiteChrome>
  );
}
